import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import {
  RAF_DOC_LABELS,
  generateAllRafDocuments,
  generateRafDocument,
} from '../../../actions/matters/generateDocument';
import type { RafDocType } from '../../../actions/matters/generateDocument';
import { generateDownloadUrl } from '../../../actions/files/generateDownloadUrl';
import { findGeneratedFiles } from './documentsTabFiles';

interface DocumentsTabProps {
  matterId: string;
  docsGenerated: string[] | undefined;
  onRefresh: () => void;
}

const DOC_TYPES: RafDocType[] = [
  'raf-docx-fill',
  'raf-authorisation',
  'raf-pdf-fill',
  'raf-affidavit',
  'raf-consultation-note',
  'raf-checklist',
];

const DocumentsTab: React.FC<DocumentsTabProps> = ({
  matterId,
  docsGenerated,
  onRefresh,
}) => {
  const [generatingAll, setGeneratingAll] = useState(false);
  const [generating, setGenerating] = useState<Record<RafDocType, boolean>>(
    {} as Record<RafDocType, boolean>,
  );
  const [error, setError] = useState('');
  const [statusText, setStatusText] = useState('');

  const handleGenerate = async (docType: RafDocType) => {
    setError('');
    setGenerating((g) => ({ ...g, [docType]: true }));
    try {
      await generateRafDocument(docType, matterId);
      setStatusText(`${RAF_DOC_LABELS[docType]} queued — refresh in ~30 seconds to download.`);
      setTimeout(onRefresh, 15000);
      setTimeout(onRefresh, 45000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : `Failed to generate ${RAF_DOC_LABELS[docType]}.`,
      );
    } finally {
      setGenerating((g) => ({ ...g, [docType]: false }));
    }
  };

  const handleGenerateAll = async () => {
    setError('');
    setStatusText('');
    setGeneratingAll(true);
    try {
      const result = await generateAllRafDocuments(matterId);
      const failed = result.jobs.filter((j) => j.status === 'failed');
      if (failed.length > 0) {
        setError(`${failed.length} of ${result.total} documents failed to queue.`);
      } else {
        setStatusText(`${result.queued} of ${result.total} documents queued. Refresh in ~60 seconds to download.`);
      }
      setTimeout(onRefresh, 20000);
      setTimeout(onRefresh, 60000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to queue document generation.');
    } finally {
      setGeneratingAll(false);
    }
  };

  const handleDownload = async (fileKey: string) => {
    try {
      const { downloadUrl } = await generateDownloadUrl(fileKey);
      window.open(downloadUrl, '_blank');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download file.');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography sx={{ fontSize: '0.92rem', fontWeight: 600, color: 'var(--t1)' }}>
          RAF Document Generation
        </Typography>
        <Button
          variant="contained"
          onClick={handleGenerateAll}
          disabled={generatingAll}
          startIcon={generatingAll ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <AutoAwesomeIcon />}
          sx={{
            bgcolor: 'var(--teal)',
            color: 'var(--bg)',
            fontWeight: 600,
            fontSize: '0.82rem',
            textTransform: 'none',
            borderRadius: 'var(--radius-sm)',
            '&:hover': { bgcolor: 'var(--teal-mid)' },
            '&.Mui-disabled': { bgcolor: 'var(--s3)', color: 'var(--t4)' },
          }}
        >
          {generatingAll ? 'Queueing...' : 'Generate All Documents'}
        </Button>
      </Box>

      {statusText && (
        <Box sx={{ bgcolor: 'var(--teal-dim)', border: '1px solid var(--teal)', borderRadius: '8px', p: 1.25, mb: 2 }}>
          <Typography sx={{ fontSize: '0.8rem', color: 'var(--teal)' }}>{statusText}</Typography>
        </Box>
      )}

      {error && (
        <Box sx={{ bgcolor: 'color-mix(in srgb, var(--err) 10%, transparent)', border: '1px solid var(--err)', borderRadius: '8px', p: 1.25, mb: 2 }}>
          <Typography sx={{ fontSize: '0.8rem', color: 'var(--err)' }}>{error}</Typography>
        </Box>
      )}

      {generatingAll && <LinearProgress sx={{ mb: 2, bgcolor: 'var(--s3)', '& .MuiLinearProgress-bar': { bgcolor: 'var(--teal)' } }} />}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
          gap: 2,
        }}
      >
        {DOC_TYPES.map((docType) => {
          const files = findGeneratedFiles(docType, docsGenerated);
          const hasFiles = files.length > 0;
          const multipleFiles = files.length > 1;
          const isGenerating = generating[docType];
          return (
            <Box
              key={docType}
              sx={{
                bgcolor: 'var(--bg)',
                border: '1px solid var(--border)',
                borderRadius: '12px',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                transition: 'background-color 0.15s',
                '&:hover': { bgcolor: 'var(--s1)' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DescriptionIcon sx={{ fontSize: 20, color: hasFiles ? 'var(--ok)' : 'var(--t3)' }} />
                <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--t1)', flex: 1 }}>
                  {RAF_DOC_LABELS[docType]}
                </Typography>
              </Box>
              {hasFiles && (
                <Typography sx={{ fontSize: '0.72rem', color: 'var(--t3)', wordBreak: 'break-all' }}>
                  {files[0].key.split('/').pop()}
                </Typography>
              )}
              <Box sx={{ display: 'flex', gap: 1, mt: 'auto', flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleGenerate(docType)}
                  disabled={isGenerating || generatingAll}
                  startIcon={isGenerating ? <CircularProgress size={14} /> : undefined}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.76rem',
                    borderColor: 'var(--border)',
                    color: 'var(--t2)',
                    '&:hover': { borderColor: 'var(--teal)', color: 'var(--teal)' },
                  }}
                >
                  {hasFiles ? 'Regenerate' : 'Generate'}
                </Button>
                {files.map((file) => (
                  <Button
                    key={file.key}
                    size="small"
                    variant="contained"
                    onClick={() => handleDownload(file.key)}
                    startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
                    sx={{
                      textTransform: 'none',
                      fontSize: '0.76rem',
                      bgcolor: 'var(--teal)',
                      color: 'var(--bg)',
                      '&:hover': { bgcolor: 'var(--teal-mid)' },
                    }}
                  >
                    {multipleFiles ? file.formatLabel : 'Download'}
                  </Button>
                ))}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default DocumentsTab;
